/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { Route, Redirect } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import moment from 'moment-timezone';
import {
  GET_CURRENT_USER,
  GET_CURRENT_ORGANIZATION,
} from '../../graphql/queries';
import SideNavigation from '../SideNavigation';
import { UserContext } from '../../context/UserContext';
import Loader from '../Loader';
import * as Intercom from '../../util/Intercom';
import { AppContext } from '../../context/AppContext';
import Header from '../Header';
import AccountSetupChecklistSidebar from '../onboarding/AccountSetupChecklistSidebar';
import OnboardingHighlightTour from '../onboarding/OnboardingHighlightTour';
import OnboardingCompleteModal from '../onboarding/OnboardingCompleteModal';
import ContinueSetupWidget from '../onboarding/ContinueSetupWidget';
import { getFirstIncompleteStepId } from '../onboarding/checklistConfig';
import { isGuidedOnboardingEnabled } from '../../util/guidedOnboarding';
import AccessLevels from '../../util/AccessLevels';

const LoaderContainer = styled.div`
  position: fixed;
  width: 100vw;
  height: 100vh;
  top: 0;
  bottom: 0;
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AdminLayoutContainer = styled.div`
  height: 100vh;
  max-height: 100vh;
  width: 100%;
  overflow: auto;
`;

function AdminLayout({ component: Component, rolesAllowed, ...rest }) {
  // should combine into one query and use graphql how it is supposed to be used
  const { app, setApp } = useContext(AppContext);

  const { data, loading, error } = useQuery(GET_CURRENT_USER, {
    fetchPolicy: 'network-only',
  });
  const { data: orgData, loading: orgLoading } = useQuery(
    GET_CURRENT_ORGANIZATION,
    {
      fetchPolicy: 'network-only',
      onCompleted(data) {
        const phoneNumber = data.getCurrentOrganization?.phoneNumber || '';
        const campaignPhoneNumber = data.getCurrentOrganization?.campaignPhoneNumber || '';
        const orgTimeZone = data.getCurrentOrganization?.timezone?.name || 'America/Denver';
        const offset = data.getCurrentOrganization?.timezone?.offset || -420;
        const dstOffset = data.getCurrentOrganization?.timezone?.dstOffset || -360;
        const isDst = moment.tz(new Date(), orgTimeZone).isDST();
        const orgTimeZoneOffset = isDst ? Number(dstOffset) : Number(offset);
        const mmsEnabled = data.getCurrentOrganization?.mmsEnabled;
        const shortcode = data.getCurrentOrganization?.shortcode || '';

        setApp((app) => ({
          ...app,
          phoneNumber,
          campaignPhoneNumber,
          orgTimeZoneOffset,
          orgTimeZone,
          mmsEnabled,
          shortcode,
        }));
      },
    },
  );

  const { user, setUser } = useContext(UserContext);

  useEffect(() => {
    setApp((app) => ({ ...app, showSideNav: false }));
  }, [setApp]);

  useEffect(() => {
    if (data?.getCurrentUser) {
      setUser(data.getCurrentUser);
      Intercom.boot(data.getCurrentUser, data.getCurrentOrganization, false);
    }
  }, [data, setUser]);

  useEffect(() => {
    if (orgData?.getCurrentOrganization) {
      setUser((old) => ({
        ...old,
        organization: orgData.getCurrentOrganization,
      }));
    }
  }, [orgData, setUser]);

  useEffect(() => {
    const org = orgData?.getCurrentOrganization;
    const orgId = org?._id;
    const progress = org?.onboardingChecklistProgress;
    const role = user?.currentOrganizationRole;

    if (!orgId || !user?._id) return;
    if (!isGuidedOnboardingEnabled(user)) return;
    if (role !== AccessLevels.Admin && role !== AccessLevels.SuperAdmin) return;
    if (!progress || progress.allComplete) return;
    if (app?.onboarding?.autoNavigatedForOrganizationId === orgId) return;

    const firstIncomplete = getFirstIncompleteStepId(progress, org);
    if (firstIncomplete == null) return;

    setApp((prev) => ({
      ...prev,
      onboarding: {
        ...prev.onboarding,
        autoNavigatedForOrganizationId: orgId,
        checklistSidebarOpen: false,
        focusedChecklistStepId: firstIncomplete,
        checklistDeferTourUntilStepClick: false,
        tourScreenIndex: 0,
        tourSuppressForStepId: null,
        tourTargetMissStepId: null,
        continueWidgetAfterStepId: null,
        tourRetryKey: (prev.onboarding?.tourRetryKey ?? 0) + 1,
      },
    }));
  }, [
    orgData,
    user?._id,
    user?.currentOrganizationRole,
    user?.guidedOnboardingEnabled,
    app?.onboarding?.autoNavigatedForOrganizationId,
    setApp,
  ]);

  if (
    loading
    || orgLoading
    || (!data && !data?.currentUser && !error)
    || (!user && !error)
  ) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  if (error) {
    console.error(error);
  }

  const userHasRequiredRole = rolesAllowed.includes(
    user?.currentOrganizationRole,
  );
  const isAuthed = !!localStorage.getItem('CHECK_THIS_OUT_AUTH_TOKEN');
  const activeSubscriber = orgData?.getCurrentOrganization?.activeSubscriber
    || user?.currentOrganizationRole === 'SuperAdmin';

  return (
    <Route
      exact
      {...rest}
      render={(matchProps) => (isAuthed && userHasRequiredRole && activeSubscriber ? (
        <AdminLayoutContainer>
          <Header currentOrganisation={orgData.getCurrentOrganization} />
          <SideNavigation />
          <AccountSetupChecklistSidebar />
          <OnboardingHighlightTour />
          <OnboardingCompleteModal />
          <ContinueSetupWidget />
          <Component {...matchProps} />
        </AdminLayoutContainer>
      ) : (
        <>
          {!isAuthed || !userHasRequiredRole ? (
            <Redirect to="/login" />
          ) : (
            <Redirect to="/enterPayment" /> // organization does not have an active subscription
          )}
        </>
      ))}
    />
  );
}

export default AdminLayout;
